import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Server, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  RefreshCw,
  Database,
  HardDrive,
  FileImage,
  Wrench,
  Terminal
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SystemStatus() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);

  useEffect(() => {
    fetchHealth();
  }, []);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/system/health`);
      setHealth(response.data);
    } catch (error) {
      console.error("Failed to fetch health:", error);
      toast.error("Failed to check system status");
    } finally {
      setLoading(false);
    }
  };

  const attemptRepair = async () => {
    setRepairing(true);
    try {
      const response = await axios.post(`${API}/system/repair`);
      toast.success("Repair attempted");
      
      if (response.data.manual_fixes_required?.length > 0) {
        toast.info("Some issues require manual intervention", {
          description: "Check the repair results for details"
        });
      }
      
      // Refresh health status
      await fetchHealth();
    } catch (error) {
      toast.error("Repair failed");
    } finally {
      setRepairing(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === "healthy") return <CheckCircle className="w-6 h-6 text-[#059669]" />;
    if (status === "degraded") return <AlertTriangle className="w-6 h-6 text-[#D97706]" />;
    return <XCircle className="w-6 h-6 text-[#DC2626]" />;
  };

  const getStatusColor = (status) => {
    if (status === "healthy") return "bg-[#059669]/10 border-[#059669]/20";
    if (status === "degraded") return "bg-[#D97706]/10 border-[#D97706]/20";
    return "bg-[#DC2626]/10 border-[#DC2626]/20";
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>
            System Status
          </h1>
          <p className="text-[#5C5C5C]">Monitor system health and dependencies</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchHealth} disabled={loading} className="rounded-full">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={attemptRepair} disabled={repairing} className="rounded-full bg-primary">
            <Wrench className={`w-4 h-4 mr-2 ${repairing ? 'animate-spin' : ''}`} />
            Auto Repair
          </Button>
        </div>
      </div>

      {/* Recent System Updates */}
      <Card className="border-2 border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2 text-blue-800">
            <Server className="w-5 h-5" />
            Recent System Updates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
              <div>
                <p className="font-medium text-[#1A1A1A]">PyMuPDF Integration</p>
                <p className="text-sm text-[#5C5C5C]">Added pure Python PDF processing - no system dependencies required for slide extraction</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
              <div>
                <p className="font-medium text-[#1A1A1A]">IP & Device Tracking</p>
                <p className="text-sm text-[#5C5C5C]">User profile access now tracks IP address, location, browser, OS, and device type</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
              <div>
                <p className="font-medium text-[#1A1A1A]">Tougher Scoring System</p>
                <p className="text-sm text-[#5C5C5C]">AI feedback now uses stricter evaluation criteria - 90+ is exceptional (TED Talk quality)</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
              <div>
                <p className="font-medium text-[#1A1A1A]">My Library Feature</p>
                <p className="text-sm text-[#5C5C5C]">Users can now filter between "All Library" and "My Library" in their profile</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
              <div>
                <p className="font-medium text-[#1A1A1A]">LibreOffice Status</p>
                <p className="text-sm text-[#5C5C5C]">LibreOffice is now optional - PyMuPDF handles PDF extraction without system dependencies</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : health ? (
        <>
          {/* Overall Status */}
          <Card className={`border-2 ${getStatusColor(health.status)}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {getStatusIcon(health.status)}
                  <div>
                    <h2 className="text-xl font-bold capitalize">{health.status}</h2>
                    <p className="text-[#5C5C5C]">
                      {health.status === "healthy" 
                        ? "All systems operational" 
                        : health.status === "degraded"
                          ? "Some features may not work properly"
                          : "Critical issues detected"}
                    </p>
                  </div>
                </div>
                <Server className="w-12 h-12 text-[#9CA3AF]" />
              </div>
            </CardContent>
          </Card>

          {/* Issues */}
          {health.issues?.length > 0 && (
            <Card className="border-[#DC2626]/20 bg-[#DC2626]/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2 text-[#DC2626]">
                  <AlertTriangle className="w-5 h-5" />
                  Issues Detected ({health.issues.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {health.issues.map((issue, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[#5C5C5C]">
                      <XCircle className="w-4 h-4 text-[#DC2626] mt-0.5 flex-shrink-0" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {health.notes?.length > 0 && (
            <Card className="border-[#D97706]/20 bg-[#D97706]/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2 text-[#D97706]">
                  <AlertTriangle className="w-5 h-5" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {health.notes.map((note, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[#5C5C5C]">
                      <CheckCircle className="w-4 h-4 text-[#D97706] mt-0.5 flex-shrink-0" />
                      {note}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Component Status Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* PyMuPDF - Primary PDF Processing */}
            <Card className="stats-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#5C5C5C] font-medium">PyMuPDF</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">PDF slide extraction (primary)</p>
                  </div>
                  {health.checks?.pymupdf?.installed ? (
                    <CheckCircle className="w-5 h-5 text-[#059669]" />
                  ) : (
                    <XCircle className="w-5 h-5 text-[#DC2626]" />
                  )}
                </div>
                {health.checks?.pymupdf?.version && (
                  <p className="text-xs text-[#5C5C5C] mt-2">
                    v{health.checks.pymupdf.version}
                  </p>
                )}
                {health.checks?.pymupdf?.note && (
                  <p className="text-xs text-[#059669] mt-1">
                    {health.checks.pymupdf.note}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* python-pptx */}
            <Card className="stats-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#5C5C5C] font-medium">python-pptx</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">PowerPoint processing</p>
                  </div>
                  {health.checks?.python_pptx?.installed ? (
                    <CheckCircle className="w-5 h-5 text-[#059669]" />
                  ) : (
                    <XCircle className="w-5 h-5 text-[#DC2626]" />
                  )}
                </div>
                {health.checks?.python_pptx?.note && (
                  <p className="text-xs text-[#059669] mt-2">
                    {health.checks.python_pptx.note}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* LibreOffice */}
            <Card className="stats-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#5C5C5C] font-medium">LibreOffice</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">PPT/PPTX conversion (optional)</p>
                  </div>
                  {health.checks?.libreoffice?.installed ? (
                    <CheckCircle className="w-5 h-5 text-[#059669]" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-[#D97706]" />
                  )}
                </div>
                {health.checks?.libreoffice?.installed && health.checks?.libreoffice?.version ? (
                  <p className="text-xs text-[#5C5C5C] mt-2 truncate" title={health.checks.libreoffice.version}>
                    {health.checks.libreoffice.version}
                  </p>
                ) : (
                  <p className="text-xs text-[#D97706] mt-2">
                    {health.checks?.libreoffice?.note || "Optional - PyMuPDF handles PDF"}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Poppler Utils */}
            <Card className="stats-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#5C5C5C] font-medium">Poppler Utils</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">PDF extraction (fallback)</p>
                  </div>
                  {health.checks?.poppler_utils?.installed ? (
                    <CheckCircle className="w-5 h-5 text-[#059669]" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-[#D97706]" />
                  )}
                </div>
                {health.checks?.poppler_utils?.path ? (
                  <p className="text-xs text-[#5C5C5C] mt-2 truncate" title={health.checks.poppler_utils.path}>
                    {health.checks.poppler_utils.path}
                  </p>
                ) : (
                  <p className="text-xs text-[#D97706] mt-2">
                    {health.checks?.poppler_utils?.note || "Optional - PyMuPDF is primary"}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Database */}
            <Card className="stats-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#5C5C5C] font-medium">Database</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">MongoDB connection</p>
                  </div>
                  {health.checks?.database?.connected ? (
                    <CheckCircle className="w-5 h-5 text-[#059669]" />
                  ) : (
                    <XCircle className="w-5 h-5 text-[#DC2626]" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Database className="w-4 h-4 text-[#9CA3AF]" />
                  <span className="text-xs text-[#5C5C5C]">
                    {health.checks?.database?.connected ? "Connected" : "Disconnected"}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Uploads Directory */}
            <Card className="stats-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#5C5C5C] font-medium">Uploads Directory</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">File storage</p>
                  </div>
                  {health.checks?.uploads_dir?.exists && health.checks?.uploads_dir?.writable ? (
                    <CheckCircle className="w-5 h-5 text-[#059669]" />
                  ) : (
                    <XCircle className="w-5 h-5 text-[#DC2626]" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <HardDrive className="w-4 h-4 text-[#9CA3AF]" />
                  <span className="text-xs text-[#5C5C5C]">
                    {health.checks?.uploads_dir?.writable ? "Writable" : "Not writable"}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Slides Directory */}
            <Card className="stats-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#5C5C5C] font-medium">Slides Directory</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Extracted slides</p>
                  </div>
                  {health.checks?.slides_dir?.exists && health.checks?.slides_dir?.writable ? (
                    <CheckCircle className="w-5 h-5 text-[#059669]" />
                  ) : (
                    <XCircle className="w-5 h-5 text-[#DC2626]" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <FileImage className="w-4 h-4 text-[#9CA3AF]" />
                  <span className="text-xs text-[#5C5C5C]">
                    {health.checks?.slides_dir?.writable ? "Writable" : "Not writable"}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <Card className="stats-card">
          <CardContent className="p-12 text-center">
            <Server className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
            <p className="text-[#5C5C5C]">Failed to load system status</p>
            <Button onClick={fetchHealth} variant="outline" className="mt-4 rounded-full">
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
